import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    IconButton,
    Box,
    useMediaQuery,
    useTheme,
    Avatar,
    Chip,
    Typography,
    Tooltip,
    FormControl,
    Select,
    MenuItem as MuiMenuItem,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Dashboard,
    People,
    Assignment,
    Logout,
    CloudDone,
    CloudOff,
    Person,
    Class,
    Apartment,
    TrendingUp,
    ManageAccounts
} from '@mui/icons-material';
import {
    getActiveSchoolGoogleIntegration
} from '../services/googleDriveService';
import { getSchools, getActiveSchoolId, setActiveSchoolId } from '../services/dataService';
import useActiveSchoolId from '../hooks/useActiveSchoolId';

const Navbar = ({ onLogout, user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [googleConnected, setGoogleConnected] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [schools, setSchools] = useState([]);
    const activeSchoolId = useActiveSchoolId();
    const [schoolGoogleEmail, setSchoolGoogleEmail] = useState("");
    const isAdmin = user?.role === "admin";
    const isMentor = user?.role === "mentor";
    const visibleSchools = useMemo(
        () => (isMentor ? schools.filter((school) => school.id !== "main") : schools),
        [isMentor, schools]
    );

    const getNavigationItems = () => {
        const mentorItems = [
            { label: '授業記録', icon: <Assignment />, path: '/class-record' },
            { label: '生徒管理', icon: <People />, path: '/students' },
            { label: '統計データ', icon: <TrendingUp />, path: '/statistics-data' }
        ];
        const adminItems = [
            { label: 'ダッシュボード', icon: <Dashboard />, path: '/dashboard' },
            ...mentorItems
        ];
        const baseItems = isMentor ? mentorItems : adminItems;

        if (isAdmin) {
            baseItems.push({ label: '教室管理', icon: <Apartment />, path: '/schools' });
            baseItems.push({ label: 'アカウント', icon: <ManageAccounts />, path: '/accounts' });
            baseItems.push({ label: 'カリキュラム管理', icon: <Class />, path: '/courses' });
        }

        return baseItems;
    };

    const navigationItems = getNavigationItems();

    useEffect(() => {
        const checkGoogleConnection = async () => {
            try {
                const schoolIntegration = await getActiveSchoolGoogleIntegration();
                const schoolConnected = Boolean(schoolIntegration?.connected);
                setGoogleConnected(schoolConnected);
                setSchoolGoogleEmail(schoolIntegration?.googleEmail || "");
            } catch (error) {
                setGoogleConnected(false);
                setSchoolGoogleEmail("");
            }
        };

        checkGoogleConnection();
        const interval = setInterval(() => {
            checkGoogleConnection();
        }, 30000);

        return () => clearInterval(interval);
    }, [activeSchoolId]);

    useEffect(() => {
        let isAlive = true;
        const loadSchools = async () => {
            try {
                const data = await getSchools();
                if (isAlive) {
                    setSchools(data);
                    const currentSchoolId = getActiveSchoolId();
                    const candidateSchools = isMentor
                        ? data.filter((school) => school.id !== "main")
                        : data;
                    if (
                        candidateSchools.length > 0 &&
                        !candidateSchools.some((school) => school.id === currentSchoolId)
                    ) {
                        const preferredSchoolId =
                            isMentor &&
                            user?.schoolId &&
                            user.schoolId !== "main" &&
                            candidateSchools.some((school) => school.id === user.schoolId)
                                ? user.schoolId
                                : candidateSchools[0].id;
                        const fallbackSchoolId = preferredSchoolId;
                        setActiveSchoolId(fallbackSchoolId);
                    }
                }
            } catch (error) {
                console.error('教室一覧の取得に失敗しました:', error);
            }
        };
        loadSchools();
        return () => {
            isAlive = false;
        };
    }, [isMentor, user?.schoolId]);

    useEffect(() => {
        if (visibleSchools.length === 0) return;
        if (visibleSchools.some((school) => school.id === activeSchoolId)) return;
        const fallbackSchoolId = visibleSchools[0].id;
        setActiveSchoolId(fallbackSchoolId);
    }, [activeSchoolId, visibleSchools]);

    const getCurrentValue = () => {
        const currentItem = navigationItems.find(item =>
            item.path === location.pathname ||
            (item.path === '/class-record' && location.pathname.startsWith('/class-record')) ||
            (item.path === '/statistics-data' && location.pathname.startsWith('/statistics-data')) ||
            (item.path === '/dashboard' && location.pathname === '/')
        );
        return currentItem ? navigationItems.indexOf(currentItem) : 0;
    };

    const handleChange = (event, newValue) => {
        if (newValue < navigationItems.length) {
            navigate(navigationItems[newValue].path);
        }
    };

    const handleUserMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleUserMenuClose();
        onLogout();
    };

    const handleSchoolChange = (event) => {
        const nextSchoolId = event.target.value;
        if (nextSchoolId === activeSchoolId) return;

        try {
            setActiveSchoolId(nextSchoolId);
        } catch (error) {
            console.error('教室切替に失敗しました:', error);
        }
    };

    const renderUserInfo = () => {
        if (!user) return null;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                {/* Google Drive連携状態 */}
                <Tooltip
                    title={
                        googleConnected
                            ? schoolGoogleEmail
                                ? `教室Google連携中: ${schoolGoogleEmail}`
                                : 'Google Drive 連携中'
                            : 'Google Drive 未連携'
                    }
                >
                    <Chip
                        icon={googleConnected ? <CloudDone /> : <CloudOff />}
                        label={isMobile ? '' : (googleConnected ? '連携中' : '未連携')}
                        color={googleConnected ? 'success' : 'default'}
                        size="small"
                        variant={googleConnected ? 'filled' : 'outlined'}
                    />
                </Tooltip>

                {/* ユーザー情報 */}
                <Tooltip title={`${user.name} (${user.role === 'admin' ? '管理者' : 'メンター'})`}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={handleUserMenuOpen}
                    >
                        <Avatar
                            src={user.picture || null}
                            sx={{ width: 32, height: 32 }}
                        >
                            {!user.picture && user.name.charAt(0)}
                        </Avatar>
                        {!isMobile && (
                            <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {user.name}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>
            </Box>
        );
    };

    return (
        <>
            <Paper sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BottomNavigation
                        value={getCurrentValue()}
                        onChange={handleChange}
                        sx={{ flexGrow: 1, minHeight: isMobile ? 68 : 74 }}
                        showLabels
                    >
                        {navigationItems.map((item, index) => (
                            <BottomNavigationAction
                                key={index}
                                label={item.label}
                                icon={item.icon}
                                sx={{
                                    minWidth: isMobile ? 84 : 94,
                                    py: isMobile ? 0.5 : 1,
                                    '& .MuiBottomNavigationAction-label': {
                                        fontSize: isMobile ? '0.72rem' : '0.82rem',
                                        fontWeight: 700,
                                        lineHeight: 1.2
                                    }
                                }}
                            />
                        ))}
                    </BottomNavigation>

                    {visibleSchools.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: isMobile ? 96 : 160, mr: 1 }}>
                            <Select
                                value={
                                    visibleSchools.some((school) => school.id === activeSchoolId)
                                        ? activeSchoolId
                                        : visibleSchools[0]?.id || ""
                                }
                                onChange={handleSchoolChange}
                                displayEmpty
                            >
                                {visibleSchools.map((school) => (
                                    <MuiMenuItem key={school.id} value={school.id}>
                                        {isMobile ? school.id : school.name || school.id}
                                    </MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {renderUserInfo()}

                    <IconButton
                        color="error"
                        onClick={handleLogout}
                        sx={{ mr: 1 }}
                        title="ログアウト"
                    >
                        <Logout />
                    </IconButton>
                </Box>
            </Paper>

            {/* ユーザーメニュー */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <MenuItem disabled>
                    <ListItemIcon>
                        <Person />
                    </ListItemIcon>
                    <ListItemText
                        primary={user?.name}
                        secondary={`${user?.role === 'admin' ? '管理者' : 'メンター'}`}
                    />
                </MenuItem>
                
                <Divider />
                
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <Logout />
                    </ListItemIcon>
                    <ListItemText primary="ログアウト" />
                </MenuItem>
            </Menu>
        </>
    );
};

export default Navbar; 
